import { useEffect, useRef, useState } from "react";
import { getAllPlayerInfo, getPlayerInfo } from "../tools/PlayerTools";
import { useLocation, useNavigate } from "react-router-dom";
import PlayerInfoContext from "../context/PlayerInfoContext";
import Controls from "./Controls";
import { AmountCard } from "./AmountCard";
import { jwtDecode } from "jwt-decode";
import { getStockPrices } from "../tools/StockTools";
import { StockPrices } from "./StockPrices";
import PlayerCards from "./PlayerCards";
import { PerPlayerStockData } from "./PerPlayerStockData";
import { UpdateCard } from "./UpdateCard";
import { getRoundLogs } from "../tools/RoundTools";
import { RoundLogs } from "./RoundLogs";
import { getCollectedCards } from "../tools/CardTools";
import { getFinalResults } from "../tools/GameTools";
import { FinalResults } from "./FinalResults";
import Spinner from "./Spinner";
import { PlayerHoldings } from "./PlayerHoldings";
import PlayerOptions from "./PlayerOptions";

function Dashboard() {
    const [playerInfo, setPlayerInfo] = useState(null);
    const [stockPrices, setStockPrices] = useState(null);
    const [allPlayerInfo, setAllPlayerInfo] = useState(null);
    const [width, setWidth] = useState(window.innerWidth);
    const [update, setUpdate] = useState(null);
    const [roundLogs, setRoundLogs] = useState([]);
    const [collectedCards, setCollectedCards] = useState([]);
    const [finalResults, setFinalResults] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadingMessage, setLoadingMessage] = useState("Getting into market");
    const [statement, setStatement] = useState([]);

    const wakeLockRef = useRef(null);

    const navigate = useNavigate();
    const location = useLocation();

    const requestWakeLock = async () => {
        try {
            if ('wakeLock' in navigator) {
                wakeLockRef.current = await navigator.wakeLock.request('screen');

                // Re-activate on visibility change
                document.addEventListener('visibilitychange', async () => {
                    if (document.visibilityState === 'visible' && !wakeLockRef.current) {
                        wakeLockRef.current = await navigator.wakeLock.request('screen');
                    }
                });
            } else {
                console.warn('Wake Lock API not supported');
            }
        } catch (err) {
            console.error(`${err.name}, ${err.message}`);
        }
    };

    const handleVisibilityChange = () => {
        if (document.visibilityState === "visible") {
            fetchPlayerInfo();
            fetchStockPrices();
        }
        connectToWebSocket();
    }

    const connectToWebSocket = () => {
        const socket = new WebSocket("wss://dse-web-socket-server.onrender.com");
        socket.onopen = () => {
            const details = jwtDecode(localStorage.getItem("token"));
            socket.send("playerId " + details.user_id);
        }
        socket.onmessage = (event) => {
            if (event.data === "refresh_player_info") {
                fetchPlayerInfo();
            } else if (event.data === "refresh_stock_prices") {
                fetchStockPrices();
            } else if (event.data.startsWith("update: ")) {
                setUpdate(event.data.substring("update: ".length));
                if (event.data.endsWith(" joined")) {
                    fetchPlayerInfo();
                }
                if (!event.data.includes("is viewing")) {
                    setTimeout(() => {
                        setUpdate(null);
                    }, 5000);
                }
            } else if (event.data === "distributing_cards") {
                setLoading(true);
                setLoadingMessage("Distributing cards")
            } else if (event.data === "collecting_cards") {
                setLoading(true);
                setLoadingMessage("Collecting cards")
            }
        }
    }

    useEffect(() => {
        requestWakeLock();
        const handleResize = () => setWidth(window.innerWidth);
        window.addEventListener("resize", handleResize);
        fetchPlayerInfo();
        fetchStockPrices();
        connectToWebSocket();
        document.addEventListener("visibilitychange", handleVisibilityChange);
        return () => {
            document.removeEventListener("visibilitychange", handleVisibilityChange);
            window.removeEventListener("resize", handleResize)
            if (wakeLockRef.current) {
                wakeLockRef.current.release();
                wakeLockRef.current = null;
            }
        };
        // eslint-disable-next-line    
    }, []);

    const fetchStockPrices = () => {
        getStockPrices().then(data => {
            setStockPrices(data)
        }).catch(e => { });
    }

    const fetchPlayerInfo = () => {
        getPlayerInfo().then(data => {
            if (data.error || (data.gameId && data.gameId !== location.pathname.substring(location.pathname.lastIndexOf("/") + 1))) {
                navigate("/");
            } else if (!data.gameId) {
                setLoading(true);
                setLoadingMessage("taking little longer...");
            }
            else {
                setPlayerInfo(data);
                document.title = `Game #${data.gameNumber}`;
                setLoading(false);
            }
            if (data.currentPlayer) {
                setUpdate(data.currentPlayer.photoUrl + " " + data.currentPlayer.name + " is playing");
            }
            if (width >= 1080 && data.viewCollectedCards) {
                fetchCollectedCards();
            }
            if (data.gameEnded) {
                getFinalResults().then(data => {
                    if (!data.error) {
                        setFinalResults(data.results);
                        setStatement(data.statement)
                    }
                }).catch(error => { })
            }
        }).catch(error => {
            navigate("/");
        })
        if (width >= 1080) {
            fetchAllPlayerHoldings();
            fetchRoundLogs();
        }
    }

    const fetchRoundLogs = () => {
        getRoundLogs().then(data => {
            if (!data.error) {
                setRoundLogs(data.logs);
            }
        })
    }

    const fetchCollectedCards = () => {
        setCollectedCards([]);
        getCollectedCards().then(data => {
            if (!data.error) {
                setCollectedCards(data.collectedCards);
            }
        }
        ).catch(e => { });
    }
    const fetchAllPlayerHoldings = () => {
        getAllPlayerInfo(true).then(data => {
            if (!data.error) {
                setAllPlayerInfo(data);
            }
        }).catch(e => { });
    }

    if (!playerInfo || loading) {
        return <div style={{ width: "100vw", height: "100vh", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column" }}>
            <h1>{loadingMessage}</h1>
            <Spinner />
        </div>
    }

    return (
        <PlayerInfoContext.Provider value={playerInfo}>
            <div>
                <div style={{ position: "sticky", zIndex: 1000, top: 0, left: 0, right: 0, backgroundColor: "white", paddingBottom: "18px" }}>
                    <Controls size={`${width >= 1080 ? "lg" : "sm"}`} />
                    <div style={{ display: "flex" }}>
                        <AmountCard title="Your Balance" amount={playerInfo?.balance || 0} />
                        <AmountCard title="Your Wealth" amount={playerInfo?.wealth || 0} />
                    </div>
                    {!playerInfo.chance && !playerInfo.applyShareSuspend && !playerInfo.waitingForNextRound && update && <UpdateCard photoUrl={update.substring(0, update.indexOf(" "))} message={update.substring(update.indexOf(" ") + 1)} size={`${width >= 1080 ? "lg" : "sm"}`} />}
                </div>
                <div style={{ display: `${width >= 1080 ? "flex" : "block"}`, paddingBottom: "2.5vw", width: "100%", overflowX: "auto" }}>
                    {finalResults && <FinalResults allPlayerInfo={finalResults} statement={statement} size={`${width >= 1080 ? "lg" : "sm"}`} />}
                    {width >= 1080 && playerInfo?.viewCollectedCards && <PlayerCards size="lg" collectedCards={collectedCards} />}
                    {width < 1080 && <PlayerHoldings player={playerInfo} size={"sm"} self={true} />}
                    <StockPrices stockPrices={stockPrices} size={`${width >= 1080 ? "lg" : "sm"}`} />
                    {playerInfo.options.length > 0 && <PlayerOptions size={`${width >= 1080 ? "lg" : "sm"}`} options={playerInfo.options} />}
                    <PlayerCards size={`${width >= 1080 ? "lg" : "sm"}`} />
                    {width >= 1080 && allPlayerInfo && <PerPlayerStockData allPlayerInfo={allPlayerInfo.details} size="lg" />}
                    {width >= 1080 && <RoundLogs logs={roundLogs} size="lg" />}
                </div>
            </div>
        </PlayerInfoContext.Provider>

    )
}

export default Dashboard;